import type {SelectPanelGroupedListProps} from '@primer/react'
import type {Reviewer, SuggestedReviewer} from '../../../../page-data/payloads/review-requests'

export const SUGGESTIONS_GROUP_ID = 'suggestions'
export const EVERYONE_ELSE_GROUP_ID = 'everyone-else'

export type ReviewerGroupId = typeof SUGGESTIONS_GROUP_ID | typeof EVERYONE_ELSE_GROUP_ID

type GroupMetadata = SelectPanelGroupedListProps['groupMetadata']

const SUGGESTIONS_HEADER: GroupMetadata[number] = {
  groupId: SUGGESTIONS_GROUP_ID,
  header: {title: 'Suggestions', variant: 'filled'},
}
const EVERYONE_ELSE_HEADER: GroupMetadata[number] = {
  groupId: EVERYONE_ELSE_GROUP_ID,
  header: {title: 'Everyone else', variant: 'filled'},
}

/**
 * A reviewer's `displayLogin` is unique across the picker and doubles as the
 * SelectPanel item id, so selection state is keyed entirely by login. Each
 * option also carries its `reviewerId`/`isTeam`, making it fully self-describing:
 * the close-time mutation reads them straight off the option, so no separate
 * login → reviewer lookup table is needed.
 */
export interface ReviewerOption {
  id: string
  login: string
  name: string | null
  /** Suggestion reason; rendered as a block-variant description. */
  reason?: string
  avatarUrl: string
  reviewerId: number
  isTeam: boolean
  group: ReviewerGroupId
  /** Whether the reviewer is currently requested on the PR. */
  requested: boolean
}

/** Merge currently-requested reviewers (pending requests + completed reviews), deduped by login. */
export function mergeCurrentReviewers(reviewers: {
  pendingReviewRequests: ReadonlyArray<{reviewer: Reviewer}>
  completedReviews: ReadonlyArray<{reviewer: Reviewer}>
}): Reviewer[] {
  const byLogin = new Map<string, Reviewer>()
  for (const request of reviewers.pendingReviewRequests) {
    byLogin.set(request.reviewer.displayLogin, request.reviewer)
  }
  for (const review of reviewers.completedReviews) {
    byLogin.set(review.reviewer.displayLogin, review.reviewer)
  }
  return Array.from(byLogin.values())
}

export function matchesFilter(query: string, ...haystacks: Array<string | null | undefined>): boolean {
  const needle = query.trim().toLowerCase()
  if (needle.length === 0) return true
  return haystacks.some(v => v != null && v.toLowerCase().includes(needle))
}

/** Stable partition that floats currently-requested entries to the front. */
export function partitionRequestedFirst<T>(
  list: readonly T[],
  getLogin: (item: T) => string,
  requestedLogins: ReadonlySet<string>,
): T[] {
  const selected: T[] = []
  const unselected: T[] = []
  for (const item of list) {
    if (requestedLogins.has(getLogin(item))) selected.push(item)
    else unselected.push(item)
  }
  return [...selected, ...unselected]
}

function toSuggestionOption(
  {reviewer, description}: SuggestedReviewer,
  requestedLogins: ReadonlySet<string>,
): ReviewerOption {
  return {
    id: reviewer.displayLogin,
    login: reviewer.displayLogin,
    name: reviewer.name,
    reason: description || undefined,
    avatarUrl: reviewer.avatarUrl,
    reviewerId: reviewer.id,
    isTeam: reviewer.reviewerType === 'Team',
    group: SUGGESTIONS_GROUP_ID,
    requested: requestedLogins.has(reviewer.displayLogin),
  }
}

function toEveryoneElseOption(reviewer: Reviewer, requestedLogins: ReadonlySet<string>): ReviewerOption {
  return {
    id: reviewer.displayLogin,
    login: reviewer.displayLogin,
    name: reviewer.name,
    avatarUrl: reviewer.avatarUrl,
    reviewerId: reviewer.id,
    isTeam: reviewer.reviewerType === 'Team',
    group: EVERYONE_ELSE_GROUP_ID,
    requested: requestedLogins.has(reviewer.displayLogin),
  }
}

type Props = {
  suggestedReviewers: readonly SuggestedReviewer[]
  searchedReviewers: readonly Reviewer[]
  currentReviewers: readonly Reviewer[]
  currentLogins: ReadonlySet<string>
  filter: string
}

/**
 * Builds the ordered option list for the panel: filtered suggestions first
 * (requested floated to top), then everyone else. Suggestions are matched
 * against login/name/reason; everyone-else against login/name, mirroring the
 * server-side query parameter so typing narrows both groups consistently.
 */
export function buildReviewerOptions({
  suggestedReviewers,
  searchedReviewers,
  currentReviewers,
  currentLogins,
  filter,
}: Props): ReviewerOption[] {
  const suggestionLogins = new Set(suggestedReviewers.map(s => s.reviewer.displayLogin))

  const visibleSuggestions = suggestedReviewers.filter(({reviewer, description}) =>
    matchesFilter(filter, reviewer.displayLogin, reviewer.name, description),
  )
  const suggestionOptions = partitionRequestedFirst(
    visibleSuggestions,
    s => s.reviewer.displayLogin,
    currentLogins,
  ).map(s => toSuggestionOption(s, currentLogins))

  const everyoneElseByLogin = new Map<string, Reviewer>()
  for (const reviewer of searchedReviewers) {
    if (suggestionLogins.has(reviewer.displayLogin)) continue
    if (!matchesFilter(filter, reviewer.displayLogin, reviewer.name)) continue
    everyoneElseByLogin.set(reviewer.displayLogin, reviewer)
  }
  for (const reviewer of currentReviewers) {
    if (suggestionLogins.has(reviewer.displayLogin)) continue
    if (everyoneElseByLogin.has(reviewer.displayLogin)) continue
    if (!matchesFilter(filter, reviewer.displayLogin, reviewer.name)) continue
    everyoneElseByLogin.set(reviewer.displayLogin, reviewer)
  }
  const everyoneElseOptions = partitionRequestedFirst(
    Array.from(everyoneElseByLogin.values()),
    r => r.displayLogin,
    currentLogins,
  ).map(r => toEveryoneElseOption(r, currentLogins))

  return [...suggestionOptions, ...everyoneElseOptions]
}

/** Only render a group header when that group actually has options. */
export function buildGroupMetadata(options: readonly ReviewerOption[]): GroupMetadata {
  const metadata: GroupMetadata = []
  if (options.some(o => o.group === SUGGESTIONS_GROUP_ID)) metadata.push(SUGGESTIONS_HEADER)
  if (options.some(o => o.group === EVERYONE_ELSE_GROUP_ID)) metadata.push(EVERYONE_ELSE_HEADER)
  return metadata
}
